import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { carriersApi, driversApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface FormState {
  id?: string; name: string;
  licenseNo: string; licenseExpiry: string;
  mobile: string; carrierId: string; isActive: boolean;
}
const blank = (): FormState => ({
  name: "", licenseNo: "", licenseExpiry: "", mobile: "", carrierId: "", isActive: true,
});

export function DriversPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank());
  const [error, setError] = useState<string | null>(null);

  const carriers = useQuery({ queryKey: ["carriers"], queryFn: () => carriersApi.list({ pageSize: 200 }) });
  const query = useQuery({ queryKey: ["drivers"], queryFn: () => driversApi.list({ pageSize: 200 }) });

  const save = useMutation({
    mutationFn: async () => {
      if (form.id) {
        await driversApi.update(form.id, {
          id: form.id, name: form.name,
          licenseNo: form.licenseNo || undefined,
          licenseExpiry: form.licenseExpiry || undefined,
          mobile: form.mobile || undefined,
          carrierId: form.carrierId || undefined,
          isActive: form.isActive,
        });
      } else {
        await driversApi.create({
          name: form.name,
          licenseNo: form.licenseNo || undefined,
          licenseExpiry: form.licenseExpiry || undefined,
          mobile: form.mobile || undefined,
          carrierId: form.carrierId || undefined,
        });
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["drivers"] }); setOpen(false); },
    onError: (e) => setError(errorMessage(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => driversApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drivers"] }),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Drivers</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setForm(blank()); setError(null); setOpen(true); }}>
          New driver
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Name</TableCell><TableCell>License</TableCell>
            <TableCell>License expiry</TableCell><TableCell>Mobile</TableCell>
            <TableCell>Carrier</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {query.data?.items.map(d => (
              <TableRow key={d.id} hover>
                <TableCell><b>{d.name}</b></TableCell>
                <TableCell>{d.licenseNo ?? "—"}</TableCell>
                <TableCell>{d.licenseExpiry ? new Date(d.licenseExpiry).toLocaleDateString() : "—"}</TableCell>
                <TableCell>{d.mobile ?? "—"}</TableCell>
                <TableCell>{d.carrierName ?? "—"}</TableCell>
                <TableCell>
                  <Chip size="small" color={d.isActive ? "success" : "default"}
                    label={d.isActive ? "Active" : "Inactive"} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => {
                    setForm({
                      id: d.id, name: d.name,
                      licenseNo: d.licenseNo ?? "",
                      licenseExpiry: d.licenseExpiry ?? "",
                      mobile: d.mobile ?? "",
                      carrierId: d.carrierId ?? "",
                      isActive: d.isActive,
                    });
                    setError(null); setOpen(true);
                  }}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => { if (confirm(`Delete driver ${d.name}?`)) del.mutate(d.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? `Edit driver ${form.name}` : "New driver"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name *" value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
            <TextField label="License #" value={form.licenseNo}
              onChange={(e) => setForm(f => ({ ...f, licenseNo: e.target.value }))} />
            <TextField type="date" label="License expiry" InputLabelProps={{ shrink: true }}
              value={form.licenseExpiry}
              onChange={(e) => setForm(f => ({ ...f, licenseExpiry: e.target.value }))} />
            <TextField label="Mobile" value={form.mobile}
              onChange={(e) => setForm(f => ({ ...f, mobile: e.target.value }))} />
            <TextField select label="Carrier" value={form.carrierId}
              onChange={(e) => setForm(f => ({ ...f, carrierId: e.target.value }))}>
              <MenuItem value="">— none —</MenuItem>
              {carriers.data?.items.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>
              ))}
            </TextField>
            {form.id && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                label={form.isActive ? "Active" : "Inactive"}
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.name || save.isPending}
            onClick={() => save.mutate()}>{form.id ? "Save" : "Create"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
